import React from 'react';
import {
  Archive,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DocumentService } from '@/services/documentService';
import type { InvestorDocument } from '@/types/investor';
import { DOC_VISIBILITY_ROLES, hiddenRolesFromAllowedRoles } from '@/utils/investor/documentVisibility';
import { resolveInvestorDocumentLabel, resolveOriginalDocumentFileName } from '@/utils/investor/linkedDocumentLabel';

interface DocumentCategoryCardProps {
  /** Active documents for this lineage, sorted by version desc. */
  documents: InvestorDocument[];
  /** All versions (active + archived) for version timeline, loaded on demand. */
  allVersions?: InvestorDocument[];
  /** Whether version history is loading. */
  versionHistoryLoading?: boolean;
  /** Whether version history is expanded. */
  isExpanded?: boolean;
  /** Channel chip shown only in the combined channel view. */
  channel?: 'wholesale' | 'correspondent';
  readOnly?: boolean;
  showRestrictedBadge?: boolean;
  onEdit?: (doc: InvestorDocument) => void;
  onReplace?: (doc: InvestorDocument) => void;
  onArchive?: (doc: InvestorDocument) => void;
  onToggleVersionHistory?: () => void;
}

const ExtractionStatusDot: React.FC<{ status?: string }> = ({ status }) => {
  const config: Record<string, { color: string; tooltip: string }> = {
    completed: { color: 'bg-success', tooltip: 'AI extraction complete' },
    pending: { color: 'bg-warning animate-pulse', tooltip: 'Pending extraction' },
    extracting: { color: 'bg-primary animate-pulse', tooltip: 'Extracting' },
    failed: { color: 'bg-destructive', tooltip: 'Extraction failed' },
    skipped: { color: 'bg-muted-foreground', tooltip: 'Not a PDF — extraction skipped' }
  };

  const { color, tooltip } = config[status || 'skipped'] || config.skipped;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`h-2 w-2 flex-shrink-0 rounded-full ${color}`}
            aria-label={tooltip}
            role="img"
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const ROLE_LABELS = new Map(DOC_VISIBILITY_ROLES.map((role) => [role.value, role.label]));

const formatHiddenRoleLabels = (roles: string[]): string =>
  roles.map((role) => ROLE_LABELS.get(role) ?? role).join(', ');

const formatUploadAge = (uploadedAt: string): string => {
  const uploadedTime = new Date(uploadedAt).getTime();
  if (!Number.isFinite(uploadedTime)) {
    return 'date unavailable';
  }

  const diffMs = Math.max(0, Date.now() - uploadedTime);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  const year = 365 * day;
  const units: Array<[number, string]> = [
    [year, 'y'],
    [month, 'mo'],
    [day, 'd'],
    [hour, 'h'],
    [minute, 'm']
  ];

  for (const [unitMs, label] of units) {
    if (diffMs >= unitMs) {
      return `${Math.floor(diffMs / unitMs)}${label} ago`;
    }
  }

  return 'just now';
};

const DocumentFileIcon: React.FC<{ fileName: string; className?: string }> = ({ fileName, className }) => {
  const kind = DocumentService.getFileKind(fileName);
  if (kind === 'spreadsheet') {
    return <FileSpreadsheet className={className} aria-hidden="true" />;
  }
  if (kind === 'image') {
    return <FileImage className={className} aria-hidden="true" />;
  }
  return <FileText className={className} aria-hidden="true" />;
};

export const DocumentCategoryCard: React.FC<DocumentCategoryCardProps> = ({
  documents,
  allVersions,
  versionHistoryLoading,
  isExpanded,
  channel,
  readOnly = false,
  showRestrictedBadge = false,
  onEdit,
  onReplace,
  onArchive,
  onToggleVersionHistory
}) => {
  if (documents.length === 0) {
    return null;
  }

  const latestDoc = documents[0];
  const title = resolveInvestorDocumentLabel(latestDoc);
  const originalFileName = resolveOriginalDocumentFileName(latestDoc) || title;
  const downloadUrl = DocumentService.getDownloadUrl(latestDoc);
  const previewable = DocumentService.isPreviewable(latestDoc);
  const fileMetadata = `${originalFileName} · ${formatFileSize(latestDoc.fileSize)} · ${formatUploadAge(latestDoc.uploadedAt)}`;
  const hiddenRoles = latestDoc.hiddenRoles ?? hiddenRolesFromAllowedRoles(latestDoc.allowedRoles);
  const hiddenRoleLabels = formatHiddenRoleLabels(hiddenRoles);
  const hasOverflowActions = !readOnly && Boolean(onReplace || onEdit || onArchive);

  return (
    <div className="bg-card" data-testid="investor-document-row">
      <div className="px-3 py-1.5">
        <div className="flex min-w-0 items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <a
                href={downloadUrl}
                className="group flex min-w-0 items-center gap-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`${title} — download ${originalFileName}`}
              >
                <DocumentFileIcon
                  fileName={originalFileName}
                  className="h-4 w-4 flex-shrink-0 text-muted-foreground"
                />
                <span className="truncate text-sm font-medium text-foreground group-hover:underline">
                  {title}
                </span>
              </a>

              {channel && (
                <span className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {channel === 'wholesale' ? 'W' : 'C'}
                </span>
              )}

              {onToggleVersionHistory ? (
                <button
                  type="button"
                  onClick={onToggleVersionHistory}
                  className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  title={isExpanded ? 'Hide version history' : 'Show version history'}
                  aria-label={`${isExpanded ? 'Hide' : 'Show'} version history for ${title}`}
                  aria-expanded={isExpanded}
                >
                  v{latestDoc.version}
                  {isExpanded ? (
                    <ChevronUp className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  )}
                </button>
              ) : (
                <span className="flex-shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  v{latestDoc.version}
                </span>
              )}

              {showRestrictedBadge && hiddenRoles.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1 py-0.5 text-[10px] font-medium text-foreground sm:px-1.5"
                        aria-label="Restricted document"
                        role="img"
                        tabIndex={0}
                      >
                        <Shield className="h-3 w-3" aria-hidden="true" />
                        <span className="hidden sm:inline">Restricted</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Hidden from: {hiddenRoleLabels}. Admin and Partner can always view.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <ExtractionStatusDot status={latestDoc.extractionStatus} />
            </div>

            <a
              href={downloadUrl}
              className="mt-0.5 block truncate rounded-sm text-xs text-muted-foreground outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`${fileMetadata} — download`}
            >
              {fileMetadata}
            </a>

            {latestDoc.notes && (
              <p className="mt-0.5 truncate text-xs text-muted-foreground" title={latestDoc.notes}>
                {latestDoc.notes}
              </p>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-0.5">
            {previewable && (
              <a
                href={latestDoc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md p-1.5 text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                title="Preview document"
                aria-label={`Preview ${originalFileName}`}
              >
                <Eye className="h-4 w-4" aria-hidden="true" />
              </a>
            )}

            <a
              href={downloadUrl}
              className="rounded-md p-1.5 text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              title="Download document"
              aria-label={`Download ${originalFileName}`}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
            </a>

            {hasOverflowActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-md p-1.5 text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`More actions for ${title}`}
                    title="More document actions"
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" collisionPadding={8}>
                  {onReplace && (
                    <DropdownMenuItem onSelect={() => onReplace(latestDoc)}>
                      <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                      Replace with new version
                    </DropdownMenuItem>
                  )}
                  {onEdit && (
                    <DropdownMenuItem onSelect={() => onEdit(latestDoc)}>
                      <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                      Edit metadata
                    </DropdownMenuItem>
                  )}
                  {onArchive && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => onArchive(latestDoc)}
                      >
                        <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
                        Archive document
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-border px-3 py-2">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            Version History
            {allVersions && (
              <span className="ml-1 font-normal">({allVersions.length} versions)</span>
            )}
          </div>

          {versionHistoryLoading && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="text-xs text-muted-foreground">Loading history...</span>
            </div>
          )}

          {!versionHistoryLoading && allVersions && allVersions.length > 0 && (
            <div className="relative space-y-2 pl-5">
              <div className="absolute bottom-1 left-[7px] top-1 w-px bg-border" />

              {allVersions.map((doc, index) => {
                const versionFileName = resolveOriginalDocumentFileName(doc) || resolveInvestorDocumentLabel(doc);
                const versionDownloadUrl = DocumentService.getDownloadUrl(doc);
                const versionPreviewable = DocumentService.isPreviewable(doc);

                return (
                  <div key={doc.id} className="relative min-w-0">
                    <span
                      className={`absolute left-[-17px] top-1.5 h-2 w-2 rounded-full border-2 ${
                        index === 0
                          ? 'border-primary bg-primary'
                          : doc.isActive
                          ? 'border-primary bg-background'
                          : 'border-muted-foreground/30 bg-muted'
                      }`}
                    />

                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={index === 0 ? 'text-xs font-medium text-foreground' : 'text-xs font-medium text-muted-foreground'}>
                            v{doc.version}
                          </span>
                          {index === 0 && (
                            <span className="rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              Current
                            </span>
                          )}
                          {!doc.isActive && (
                            <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              Archived
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(doc.uploadedAt).toLocaleDateString()}
                          </span>
                          <ExtractionStatusDot status={doc.extractionStatus} />
                        </div>
                        <a
                          href={versionDownloadUrl}
                          className="mt-0.5 block truncate rounded-sm text-xs text-muted-foreground outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`${versionFileName} — download version ${doc.version}`}
                        >
                          {versionFileName}
                        </a>
                        {doc.notes && (
                          <p className="mt-0.5 truncate text-xs italic text-muted-foreground" title={doc.notes}>
                            {doc.notes}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        {versionPreviewable && (
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-md p-1 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                            title="Preview version"
                            aria-label={`Preview ${versionFileName}, version ${doc.version}`}
                          >
                            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                          </a>
                        )}
                        <a
                          href={versionDownloadUrl}
                          className="rounded-md p-1 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                          title="Download version"
                          aria-label={`Download ${versionFileName}, version ${doc.version}`}
                        >
                          <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!versionHistoryLoading && (!allVersions || allVersions.length === 0) && (
            <p className="py-2 text-xs text-muted-foreground">No version history available.</p>
          )}
        </div>
      )}
    </div>
  );
};
